import { Check, FileText, Search, Users, UserPlus } from "lucide-react"

import { Button } from "@/components/ui/button"

// PostedConfirmation — the design's posted-confirmation view (ruling 7 / WI-B2).
// Rendered after a successful submit instead of an immediate redirect to
// /projects. Everything it shows comes from the server confirmation payload
// (controller#posted_confirmation).
//
// AQ-017: the panel describes the actual work, not a hardcoded writer journey.
// It reads `payload.confirmation`, resolved server-side from the stable service
// keys (never a label string match):
//   - single standalone service → that service's roleLabel + truthful steps, and
//     its role-matched browse CTA (label + /find-freelancers?role=<role>) ONLY
//     when the service has a browsable roster role (absent for formatting,
//     content translation, plagiarism).
//   - bundle or multi-service → the combined ordered stages framed as "your
//     team" (no single role owns everything), with exactly ONE generic Find
//     freelancers CTA → /find-freelancers (no role param).
// The total is server-sourced, never a RATES figure.
//
// Props:
//   payload — { posted, reference, serviceLabel, serviceKeys, bundleTier,
//               confirmation, total, isRange, projectsPath, browseWritersPath }
//   projectName — the project's name (for the lede)
//   onGoToProjects() — navigate to /projects (or payload.projectsPath)
//   onBrowse(path) — navigate to a Find-freelancers path (role-filtered or generic)
//   lede — optional; replaces the line under the heading (the Open House flow)
//   hideNextSteps — optional; drops the next-steps card, the estimate line and
//                   the browse CTA, leaving Go to Projects

const money = (n) =>
  typeof n === "number"
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : n

// Step icon by position: the first step is the request/pickup, the last is the
// customer's review/approve, the middle steps are the work itself.
const stepIcon = (index, count) => {
  if (index === 0) return UserPlus
  if (index === count - 1) return Check
  return FileText
}

function NextStep({ icon: Icon, title, badge, children }) {
  return (
    <div className="flex items-start gap-3.5 border-b border-border px-5 py-4 last:border-b-0">
      <span className="grid size-9 shrink-0 place-items-center rounded-xl border border-border bg-secondary">
        <Icon className="size-4 text-foreground" strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <h4 className="flex items-center gap-2 font-display text-[14px] font-bold tracking-tight text-foreground">
          {title}
          {badge && (
            <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wide text-muted-foreground">
              {badge}
            </span>
          )}
        </h4>
        {children && (
          <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{children}</p>
        )}
      </div>
    </div>
  )
}

export function PostedConfirmation({
  payload = {},
  projectName,
  onGoToProjects = () => {},
  onBrowse = () => {},
  lede,
  hideNextSteps = false,
}) {
  const reference = payload.reference
  const serviceLabel = payload.serviceLabel || "Project"
  const total = payload.total
  const isRange = !!payload.isRange
  const confirmation = payload.confirmation || {}
  const isCombined = confirmation.mode === "combined"

  const estimate =
    typeof total === "number"
      ? isRange
        ? `estimate ${money(total)} (at your selected tier rate)`
        : money(total)
      : null

  const steps = Array.isArray(confirmation.steps) ? confirmation.steps : []
  const stages = Array.isArray(confirmation.stages) ? confirmation.stages : []
  const browse = confirmation.browse

  return (
    <div className="mx-auto flex max-w-[620px] flex-col items-center gap-6 py-6 text-center">
      <span className="grid size-14 place-items-center rounded-full bg-done/15 text-done">
        <Check className="size-7" strokeWidth={2.6} />
      </span>

      <div className="flex flex-col gap-2">
        <h1 className="font-display text-[24px] font-bold tracking-tight text-foreground">
          Your project is posted
        </h1>
        <p className="text-[13.5px] leading-relaxed text-muted-foreground text-pretty">
          {lede ?? (
            <>
              {projectName ? (
                <>
                  <span className="font-semibold text-foreground">{projectName}</span> is live.
                </>
              ) : (
                "Your project is live."
              )}{" "}
              {isCombined
                ? "Qualified freelancers for each stage can now send requests to take it on. You have not been charged, that happens only once you choose who to work with and start."
                : "Qualified freelancers can now send requests to take it on. You have not been charged, that happens only once you choose who to work with and start."}
            </>
          )}
        </p>
      </div>

      {reference != null && (
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[12px] font-semibold text-foreground">
          <FileText className="size-3.5 text-muted-foreground" strokeWidth={2} />
          Project {reference} &middot; {serviceLabel}
        </div>
      )}

      {!hideNextSteps && (
        <>
          <div className="w-full overflow-hidden rounded-2xl border border-border bg-card text-left">
            <div className="border-b border-border bg-accent px-5 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              {isCombined ? "Your team's workflow" : "What happens next"}
            </div>

            {isCombined
              ? stages.map((stage, index) => (
                  <NextStep
                    key={`${stage.title}-${index}`}
                    icon={Users}
                    title={stage.title}
                    badge={stage.roleLabel}
                  >
                    {stage.note ? stage.note : null}
                  </NextStep>
                ))
              : steps.map((step, index) => {
                  const Icon = stepIcon(index, steps.length)

                  return <NextStep key={index} icon={Icon} title={step} />
                })}
          </div>

          {estimate && (
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              When you choose who to work with, you confirm{" "}
              <span className="font-semibold text-foreground">{estimate}</span> and pay to kick off the
              work. Not before.
            </p>
          )}
        </>
      )}

      {/* flex-wrap is load-bearing: both buttons are whitespace-nowrap, and on a
          365px phone their combined minimum width exceeds the content column —
          without a wrap the row forces the whole card wider than the column and
          the overflow hangs off the right, shifting the screen off-centre (AU-22). */}
      <div className="flex w-full flex-wrap items-center justify-center gap-3">
        <Button variant="outline" onClick={onGoToProjects} className="gap-1.5 font-semibold">
          Go to Projects
        </Button>

        {hideNextSteps ? null : isCombined ? (
          <Button
            onClick={() => onBrowse(confirmation.findFreelancersPath)}
            className="gap-1.5 font-bold"
          >
            <Search className="size-4" strokeWidth={2.1} />
            Find freelancers
          </Button>
        ) : (
          browse && (
            <Button onClick={() => onBrowse(browse.path)} className="gap-1.5 font-bold">
              <Search className="size-4" strokeWidth={2.1} />
              {browse.label}
            </Button>
          )
        )}
      </div>
    </div>
  )
}
